/**
 * Development Mode Utility
 * Detects if the app is running in development mode
 */

/**
 * Check if the app is running in development mode
 * DEFAULT: Development mode is enabled by default
 */
export const isDevelopment = (): boolean => {
  // Check Vite's import.meta.env
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Explicitly check for production mode
    if (import.meta.env.PROD === true || import.meta.env.MODE === 'production') {
      return false;
    }
    return true; // Default to dev mode
  }

  // Fallback: check NODE_ENV (for other build tools — Node.js / Jest / CRA)
  // Use globalThis cast to avoid TS2580 in Vite's browser-target tsconfig
  const proc = (globalThis as any).process;
  if (proc && proc.env) {
    return proc.env.NODE_ENV !== 'production';
  }

  // Default to DEVELOPMENT mode (changed from production)
  return true;
};

/**
 * Extract error messages from API error response
 * Handles the Errors/errors array format from the backend
 */
export const extractApiErrorMessages = (errorDetails: any): string[] => {
  const messages: string[] = [];

  // Check for Errors array (PascalCase)
  if (errorDetails?.Errors && Array.isArray(errorDetails.Errors)) {
    errorDetails.Errors.forEach((err: any) => {
      if (err.Message) messages.push(err.Message);
      else if (err.message) messages.push(err.message);
    });
  }

  // Check for errors array (camelCase)
  if (errorDetails?.errors && Array.isArray(errorDetails.errors)) {
    errorDetails.errors.forEach((err: any) => {
      if (err.message) messages.push(err.message);
      else if (err.Message) messages.push(err.Message);
    });
  }

  return messages;
};

/**
 * Get a user-friendly error message
 * In development mode, returns the full error details
 * In production mode, returns a generic message
 */
export const getErrorMessage = (error: unknown, fallbackMessage: string = 'An error occurred'): string => {
  if (isDevelopment()) {
    // In dev mode, show detailed error information
    if (error instanceof Error) {
      return `${error.message}${error.stack ? '\n\nStack trace:\n' + error.stack : ''}`;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      // Check if this is an API error response with errors array
      const apiErrors = extractApiErrorMessages(error);
      if (apiErrors.length > 0) {
        const errorList = apiErrors.map((msg, idx) => `  ${idx + 1}. ${msg}`).join('\n');
        return `API Errors:\n${errorList}\n\nFull Details:\n${JSON.stringify(error, null, 2)}`;
      }

      try {
        return JSON.stringify(error, null, 2);
      } catch {
        return String(error);
      }
    }

    return String(error);
  } else {
    // In production mode, show generic message
    // But still extract basic error message if available
    if (error instanceof Error) {
      return error.message || fallbackMessage;
    }

    if (typeof error === 'string') {
      return error;
    }

    // Check for API errors in production too
    if (error && typeof error === 'object') {
      const apiErrors = extractApiErrorMessages(error);
      if (apiErrors.length > 0) {
        return apiErrors.join('; ');
      }
    }

    return fallbackMessage;
  }
};

/**
 * Format error for display
 * Adds development context if in dev mode
 */
export const formatErrorForDisplay = (error: unknown, context?: string): string => {
  const message = getErrorMessage(error);

  if (isDevelopment() && context) {
    return `[DEV] ${context}\n\n${message}`;
  }

  return message;
};
